/**
 * Foto nelle schede prodotto: un taglio fatto all'origine (modella senza testa o gambe, primo piano di un capo) non
 * deve restare a metà del riquadro, con il fondo oltre il taglio («galleggia»). Richieste di Donato del 19/10 e del
 * 23/10 (allora valeva solo per le foto indossate; ora per tutte le foto di tutte le schede, anche quelle dopo la prima).
 * Ogni foto si misura qui:
 *   - fondo non uniforme (foto ambientata) → data-gf-fit="cover";
 *   - soggetto che non tocca il bordo alto o basso → "contain": foto intera con i suoi margini (still life, modelle intere);
 *   - soggetto tagliato in alto o in basso:
 *       · se la parte che con «cover» uscirebbe ai lati è solo fondo → "cover": il taglio coincide col bordo della scheda;
 *       · se è un primo piano di un capo (tagliato sopra e sotto, o con il soggetto che riempie il bordo) → "cover"
 *         anche se ai lati si perde un po' di capo: è già un dettaglio, meglio pieno che sospeso;
 *       · se è un oggetto (data-gf-object: borse, scarpe, cinture… da snippets/gf-photo-object.liquid) → "contain":
 *         gli oggetti restano interi.
 * Nessuna foto viene modificata. Si legge una copia di 48 px della foto (CDN di Shopify, CORS aperto); risultati in
 * sessionStorage. Le foto dopo la prima si misurano quando la scheda entra nello schermo, prima di essere sfogliate.
 * In attesa della misura la foto è nascosta al massimo 3 s (CSS); senza JavaScript compare intera dopo 3 s.
 * Versione precedente (allineamento al bordo, scartata da Donato il 16/10): _archivio/2026-10-16_foto-allineate-al-bordo/
 */
const SELECTOR = '.card-gallery .product-media__image, .gf-pcard .gf-pcard__img';
const CARD = '.card-gallery, .gf-pcard';
const IMAGE = '.product-media__image, .gf-pcard__img';
const SIZE = 48;
const BAND = 2; // righe di bordo esaminate
const DIFF = 30; // distanza dal colore di fondo oltre la quale il pixel è soggetto
const CORNER = 40; // angolo «diverso dal fondo»: con due angoli così il fondo non è uniforme
const TOUCH = 0.08; // quota di soggetto sul bordo alto/basso per dire che la foto è tagliata
const SIDE = 0.02; // quota di soggetto tollerata in una colonna che uscirebbe dal riquadro
const CLOSE_UP = 0.35; // quota di soggetto sul bordo alto o basso oltre la quale la foto è un primo piano
const CACHE_KEY = 'gf-photo-fit-6';

let cache = {};
try {
  cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
} catch {
  cache = {};
}
let saveTimer = 0;
function remember(key, value) {
  cache[key] = value;
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
      /* spazio esaurito o memoria bloccata: si rimisura alla prossima pagina */
    }
  }, 500);
}

/** stessa foto (senza parametri di misura) nello stesso riquadro = stessa chiave */
function photoKey(src, frame, object) {
  try {
    const url = new URL(src, location.href);
    return `${url.origin}${url.pathname}@${frame.toFixed(2)}${object ? 'o' : ''}`;
  } catch {
    return '';
  }
}

function smallUrl(src) {
  const url = new URL(src, location.href);
  url.searchParams.set('width', String(SIZE));
  url.searchParams.delete('height');
  url.searchParams.delete('crop');
  return url.href;
}

/** proporzione del riquadro (larghezza / altezza) in cui sta la foto */
function frameRatio(img) {
  const w = img.clientWidth;
  const h = img.clientHeight;
  return w && h ? w / h : 0.8;
}

function analyse(src, frame, object) {
  return new Promise((resolve) => {
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    const timer = setTimeout(() => resolve(''), 8000);
    probe.onerror = () => {
      clearTimeout(timer);
      resolve('');
    };
    probe.onload = () => {
      clearTimeout(timer);
      try {
        const w = probe.naturalWidth;
        const h = probe.naturalHeight;
        if (!w || !h) return resolve('');
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0);
        const pixels = ctx.getImageData(0, 0, w, h).data;
        const at = (x, y) => {
          const i = (y * w + x) * 4;
          return [pixels[i], pixels[i + 1], pixels[i + 2], pixels[i + 3]];
        };
        const corners = [at(1, 1), at(w - 2, 1), at(1, h - 2), at(w - 2, h - 2)];
        // PNG trasparente: il soggetto è ciò che non è trasparente
        const transparent = corners.every((c) => c[3] < 20);
        // fondo = seconda più bassa per canale tra i quattro angoli (regge un angolo sporco)
        const bg = [0, 1, 2].map((ch) => corners.map((c) => c[ch]).sort((a, b) => a - b)[1]);
        const subject = (x, y) => {
          const p = at(x, y);
          return transparent ? p[3] > 20 : Math.max(...[0, 1, 2].map((ch) => Math.abs(p[ch] - bg[ch]))) > DIFF;
        };
        const far = (c) => Math.max(...[0, 1, 2].map((ch) => Math.abs(c[ch] - bg[ch]))) > CORNER;
        if (!transparent && corners.filter(far).length >= 2) return resolve('cover');
        const row = (y) => {
          let n = 0;
          for (let x = 0; x < w; x++) if (subject(x, y)) n++;
          return n / w;
        };
        const col = (x, y0, y1) => {
          let n = 0;
          for (let y = y0; y < y1; y++) if (subject(x, y)) n++;
          return n / (y1 - y0);
        };
        const top = Math.max(row(0), row(BAND - 1));
        const bottom = Math.max(row(h - 1), row(h - BAND));
        if (top <= TOUCH && bottom <= TOUCH) return resolve('contain');
        const ratio = w / h;
        // foto più alta del riquadro: si perderebbero sopra e sotto, cioè proprio il soggetto tagliato
        if (ratio < frame) return resolve('contain');
        if (ratio > frame) {
          // parte della foto che con «cover» uscirebbe dal riquadro: se c'è soggetto, si rifila solo un primo piano di un capo
          const closeUp = !object && ((top > TOUCH && bottom > TOUCH) || Math.max(top, bottom) > CLOSE_UP);
          const strip = Math.ceil((w * (1 - frame / ratio)) / 2);
          for (let x = 0; x < strip && !closeUp; x++) {
            if (col(x, 0, h) > SIDE || col(w - 1 - x, 0, h) > SIDE) return resolve('contain');
          }
        }
        resolve('cover');
      } catch {
        // copia non leggibile (CORS): foto intera
        resolve('');
      }
    };
    probe.src = smallUrl(src);
  });
}

// prima le foto in vista, poi le foto successive delle schede: le prime foto non aspettano le altre
const queue = new Set();
const later = new Set();
let running = 0;

async function process(img) {
  const src = img.getAttribute('src') || img.currentSrc;
  const frame = frameRatio(img);
  const object = Boolean(img.closest(CARD)?.hasAttribute('data-gf-object'));
  const key = photoKey(src, frame, object);
  if (!key) return void (img.dataset.gfFit = 'contain');
  let fit = cache[key];
  if (fit === undefined) {
    running++;
    fit = await analyse(src, frame, object);
    running--;
    if (fit) remember(key, fit);
    pump();
  }
  // la foto può essere cambiata nel frattempo (pallini colore): vale solo la misura dell'ultima
  if ((img.getAttribute('src') || img.currentSrc) !== src) return;
  img.dataset.gfFit = fit || 'contain';
}

function pump() {
  for (const list of [queue, later]) {
    for (const img of list) {
      if (running >= 4) return;
      list.delete(img);
      process(img);
    }
  }
}

const visible = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      visible.unobserve(entry.target);
      later.delete(entry.target);
      queue.add(entry.target);
      // con la scheda si misurano anche le foto successive, fuori vista nello scorrimento della scheda;
      // le foto nascoste (altri colori, display: none) si misurano quando compaiono, nel loro riquadro vero
      entry.target.closest(CARD)?.querySelectorAll(IMAGE).forEach((img) => {
        if (img.dataset.gfFit || queue.has(img) || !img.clientWidth) return;
        visible.unobserve(img);
        later.add(img);
      });
    }
    pump();
  },
  { rootMargin: '600px 0px' }
);

function watch(img) {
  // solo foto vere: il segnaposto SVG dei prodotti senza foto non ha nulla da misurare
  if (img.tagName !== 'IMG' || !img.matches(SELECTOR) || img.dataset.gfFit) return;
  visible.observe(img);
}

function watchAll(root) {
  if (root.matches?.(SELECTOR)) watch(root);
  root.querySelectorAll?.(SELECTOR).forEach(watch);
}

if (!window.gfPhotoFit) {
  window.gfPhotoFit = true;
  // griglie che cambiano (filtri, pagine, scorrimento infinito) e foto cambiate dai pallini colore
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') {
        const el = /** @type {HTMLElement} */ (record.target);
        if (el.tagName === 'IMG') {
          if (!el.matches(SELECTOR)) continue;
          delete el.dataset.gfFit;
          watch(el);
        } else {
          // scheda passata da still life a indossata (o viceversa) col cambio di colore
          el.querySelectorAll('img').forEach((img) => {
            delete img.dataset.gfFit;
            watch(img);
          });
        }
        continue;
      }
      record.addedNodes.forEach((node) => node.nodeType === 1 && watchAll(node));
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'data-gf-worn', 'data-gf-object'],
  });
  watchAll(document);
}
